export const SECTION_NAME = "Item";

export const INTRO_TEXT = `Items are the most important pieces of data in SOS
  Inventory. They drive everything else in the system. Items can represent
  almost anything&mdash;they do not have to be things stored in inventory.
  An item is simply something you want to track in SOS Inventory. Examples include a product you sell, a service you provide, raw materials you use in manufacturing, and your overhead expenses.`;

export const EXAMPLE_JSON = `{
    "id": 123,
    "starred": 0,
    "syncToken": 0,
    "name": "Widget14",
    "fullname": '1/4" solid aluminum widget',
    "description": "Widget used in capacitor assemblies",
    "sku": "4878932",
    "barcode": "811234000007",
    "type": "Inventory Item",
    "url": "https://www.mycompany.com/products/4878932",
    "tags": "Aluminum, Non-returnable",
    "notes": "Transportation customers only",
    "purchaseDescription": '1/4" solid aluminum widget',
    "vendorPartNumber": "WID14905",
    "customerPartNumber": null,
    "vendor": {
      "id": 23,
      "name": "Widget Master"
    },
    "bin": { id: 2, name: "A19" },
    "warranty": "Standard",
    "category": {
      "id": 8,
      "name": "Machine parts"
    },
    "class": {
      "id": 17,
      "name": "Industrial"
    },
    "incomeAccount": {
      "id": 20,
      "name": "Product Sales"
    },
    "cogsAccount": {
      "id": 4,
      "name": "Cost of Sales"
    },
    "assetAccount": {
      "id": 21,
      "name": "Inventory"
    },
    "expenseAccount": null,
    "onhand": 11.00000,
    "available": 6.00000,
    "onSO": 5.00000,
    "onSR": 0.00000,
    "rented": 0.00000,
    "onWO": -5.00000,
    "onPO": 0.00000,
    "onRMA": 0.00000,
    "reorderPoint": 0.00000,
    "maxStock": 1200.00000,
    "leadTime": 7,
    "salesPrice": 24.30000,
    "baseSalesPrice": 24.3000,
    "markupPercent": null,
    "useMarkup": false,
    "minimumPrice": 21.00000,
    "purchaseCost": 11.61000,
    "basePurchaseCost": 11.61000,
    "costBasis": 11.71000,
    "weight": 2.51100,
    "volume": 0.10800,
    "weightunit": "oz",
    "volumeunit": "cbm",
    "sublevel": null,
    "taxable": true,
    "salesTaxCode": { 
      "id": 2, 
      "name": "Sales Tax" 
    },
    "purchaseTaxCode": { 
      "id": 4, 
      "name": "Purchase Tax" 
    },
    "willSync": true,
    "updateShopify": false,
    "updateBigCommerce": false,
    "alwaysShippable": false,
    "hasImage": false,
    "serialTracking": false,
    "lotTracking": false,
    "archived": false,
    "showOnSalesForms": true,
    "showOnPurchasingForms": true,
    "showOnManufacturingForms": true,
    "lastSync": "2019-10-22T00:00:00",
    "syncMessage": "",
    "customFields": [
      {
        "id": 5,
        "name": "available in colors",
        "value": "false",
        "dataType": "Boolean"
      },
      {
        "id": 23,
        "name": "color",
        "value": "green",
        "dataType": "List"
      },
      "uoms": [
        {
          "uom": {
            "id": 5,
            "name": "box"
          },
          "conversion": 3.00000,
          "isBase": false,
          "salesPrice": null,
          "purchaseCost": 140.00000
        },
        {
          "uom": {
            "id": 1,
            "name": "Ea"
          },
          "conversion": 1.0,
          "isBase": true,
          "salesPrice": null,
          "purchaseCost": 45.00000
        }
      ],
      "priceTiers": [
        {
          "priceTier": {
            "id": 35,
            "name": "$1 Discount"
          },
          "price": 50.00000,
          "quantity": 10.00000,
          "percentDiscount": 0.00000
        }
      ],
      "summaryOnly": false,
      "imageAsBase64String": null,
      "imageChanged": false,
  }`;

export const ATTRIBUTES = [
  {
    name: "id",
    type: "integer",
    description:
      "Unique identifier for this record. ID field is ignored on create requests.",
  },
  {
    name: "starred",
    type: "integer",
    description: `Indicates if this item has been starred. A value of 0 = no star; 1 or 1-3 = starred. Star colors 
      depend on application configuration. This could be one color of star or three colors of stars. See Company Settings in the user guide for more details.`,
  },
  {
    name: "syncToken",
    type: "integer",
    description: `Indicates the current version of this record. If you receive an error when updating a record, it is because your syncToken is for an older version of the record than that which is currently in the database. Please GET the latest version prior to updating.`,
  },
  {
    name: "name",
    type: "string",
    description: `The name of this item. Must be unique within its category.`,
  },
  {
    name: "fullname",
    type: "string",
    description: `The full name of this item, which is
      [category]:[item-name]. Note that categories can be arbitrarily deep,
      so this field may look like [category]:[category]:[item-name] and so on.`,
    readOnly: true,
  },
  {
    name: "description",
    type: "string",
    description: `Default description of this item.`,
  },
  {
    name: "sku",
    type: "string",
    description: `Stockkeeping unit. A product identification code.`,
  },
  {
    name: "barcode",
    type: "string",
    description: `The string of digits represented by the item's barcode.`,
  },
  {
    name: "type",
    type: "string",
    description: `The kind of item. Must be one of the following: "Inventory Item", "Non-inventory", "Category", "Expense", "Assembly", "Kit", "Service", "Labor", "Overhead", or "Other".`,
  },
  {
    name: "url",
    type: "string",
    description: `URL to a web page about this item.`,
  },
  {
    name: "tags",
    type: "string",
    description: `Comma-separated list of tags for this item.`,
  },
  {
    name: "notes",
    type: "string",
    description: `Any relevant notes for this item.`,
  },
  {
    name: "purchaseDescription",
    type: "string",
    description: `The item description as it appears on purchasing documents.`,
  },
  {
    name: "vendorPartNumber",
    type: "string",
    description: `Used if vendor's part number for the item differs from yours.`,
  },
  {
    name: "customerPartNumber",
    type: "string",
    description: `Used if the item is sold only to a small number of customers who refer to it by a different part number than yours.`,
  },
  {
    name: "vendor",
    type: "reference",
    description: `The company or individual from whom you purchase this item.`,
  },
  {
    name: "bin",
    type: "reference",
    description: `Bin where this item is stored.`,
  },
  {
    name: "warranty",
    type: "string",
    description: `Description of the warranty for this item.`,
  },
  {
    name: "category",
    type: "reference",
    description: `Category to which this item belongs.`,
  },
  {
    name: "class",
    type: "reference",
    description: `Class to which this item belongs.`,
  },
  {
    name: "incomeAccount",
    type: "reference",
    description: `Account to which income from this item should be posted.`,
  },
  {
    name: "cogsAccount",
    type: "reference",
    description: `Account to which the cost of this item should be posted
      when it is sold.`,
  },
  {
    name: "assetAccount",
    type: "reference",
    description: `Account where this item is represented as an asset.`,
  },
  {
    name: "expenseAccount",
    type: "reference",
    description: `Account to which the cost of this item should be posted
      when it is purchased.`,
  },
  {
    name: "onhand",
    type: "decimal",
    description: `Quantity of this item currently in inventory.`,
    readOnly: true,
  },
  {
    name: "available",
    type: "decimal",
    description: `Quantity of this item available for sale.`,
    readOnly: true,
  },
  {
    name: "onSO",
    type: "decimal",
    description: `Quantity of this item on currently open sales orders.`,
    readOnly: true,
  },
  {
    name: "onSR",
    type: "decimal",
    description: `Quantity of this item on currently open sales receipts.`,
    readOnly: true,
  },
  {
    name: "rented",
    type: "decimal",
    description: `The quantity of this item currently rented to customers.`,
    readOnly: true,
  },
  {
    name: "onWO",
    type: "decimal",
    description: `Quantity of this item on currently open work orders.`,
    readOnly: true,
  },
  {
    name: "onPO",
    type: "decimal",
    description: `Quantity of this item on currently open purchase orders.`,
    readOnly: true,
  },
  {
    name: "onRMA",
    type: "decimal",
    description: `Quantity of this item on currently open return merchandise authorizations.`,
    readOnly: true,
  },
  {
    name: "reorderPoint",
    type: "decimal",
    description: `Inventory level at which this item should be reordered.`,
  },
  {
    name: "maxStock",
    type: "decimal",
    description: `Maximum quantity of this item you wish to have on hand.`,
  },
  {
    name: "leadTime",
    type: "integer",
    description: `Lead time (in days) to receive this product after ordering it.`,
  },
  {
    name: "salesPrice",
    type: "decimal",
    readOnly: true,
    description: `Calculated sales price based on if you are using markup pricing.  If you are not using markup pricing, this is the baseSalesPrice.`,
  },
  {
    name: "baseSalesPrice",
    type: "decimal",
    description: `Non-markup sales price for this item.  This isn't needed if using markup pricing.`,
  },
  {
    name: "markupPercent",
    type: "decimal",
    description: `The percentage to mark up the salesPrice from the purchaceCost or COGS if useMarkup is enabled.`,
  },
  {
    name: "useMarkup",
    type: "boolean",
    description: `If true, use markupPercent to calculate salesPrice.  If false, use baseSalesPrice for salesPrice.`,
  },
  {
    name: "minimumPrice",
    type: "decimal",
    description: `Minimum price at which you are willing to sell this item.`,
  },
  {
    name: "purchaseCost",
    type: "decimal",
    description: `The purchase cost of this item, taking into account optional default vendor pricing and bill of materials cost.`,
  },
  {
    name: "basePurchaseCost",
    type: "decimal",
    description: "The purchase cost on the item record.",
  },
  {
    name: "costBasis",
    type: "decimal",
    description: "The total actual value for the current stock of an item.",
    readOnly: true,
  },
  {
    name: "weight",
    type: "decimal",
    description: `Self-explanatory.`,
  },
  {
    name: "volume",
    type: "decimal",
    description: `Self-explanatory.`,
  },
  {
    name: "weightunit",
    type: "string",
    description: `Can be either "lb", "oz", "kg", or "g".`,
  },
  {
    name: "volumeunit",
    type: "string",
    description: `Must be "cbm" (cubic meters).`,
  },
  {
    name: "sublevel",
    type: "integer",
    description: `The number of levels between this item and the highest-level item.`,
    readOnly: true,
  },
  {
    name: "taxable",
    type: "boolean",
    description: `True if item is taxable, false if not.`,
  },
  {
    name: "salesTaxCode",
    type: "reference",
    description: `Default sales tax code. Non-US users only.`,
  },
  {
    name: "purchaseTaxCode",
    type: "reference",
    description: `Default purchase tax code. Non-US users only.`,
  },
  {
    name: "willSync",
    type: "boolean",
    description: `True if item should be synced with an external accounting system, false if not.`,
  },
  {
    name: "updateShopify",
    type: "boolean",
    description: `True if item updates should be synced back to Shopify, if the account is connected to Shopify.`,
  },
  {
    name: "updateBigCommerce",
    type: "boolean",
    description: `True if item updates should be synced back to Big Commerce, if the account is connected to Big Commerce.`,
  },
  {
    name: "alwaysShippable",
    type: "boolean",
    description: `True if item should be shown on shipments, regardless of item type. False if not.`,
  },
  {
    name: "hasImage",
    type: "boolean",
    description: `True if item a product image is available for this item, false if not.`,
    readOnly: true,
  },
  {
    name: "serialTracking",
    type: "boolean",
    description: `True if serial numbers will be tracked for this item, false if not.`,
  },
  {
    name: "lotTracking",
    type: "boolean",
    description: `True if lot numbers will be tracked for this item, false if not.`,
  },
  {
    name: "archived",
    type: "boolean",
    description: `True if item has been archived, false if not.`,
    readOnly: true,
  },
  {
    name: "showOnSalesForms",
    type: "boolean",
    description: `True if item should show on sales forms, false if not.`,
  },
  {
    name: "showOnPurchasingForms",
    type: "boolean",
    description: `True if item should show on purchasing forms, false if not.`,
  },
  {
    name: "showOnManufacturingForms",
    type: "boolean",
    description: `True if item should show on manufacturing forms, false if not.`,
  },
  {
    name: "lastSync",
    type: "date",
    newField: "New October 2nd",
    description: `The last successful sync time (GMT) for this item, if syncronizing with Quickbooks.`,
    readOnly: true,
  },
  {
    name: "syncMessage",
    type: "string",
    newField: "New October 2nd",
    description: `The sync error message if the last sync attempt failed with Quickbooks. This will be empty if synchronization is successful.`,
    readOnly: true,
  },
  {
    name: "commissionRate",
    type: "decimal",
    description: `Specifies the commission percentage that a sales representative will receive for
the sale of the item.`,
  },
  {
    name: "commissionAmount",
    type: "decimal",
    description: `Specifies the monetary amount per unit sold that a sales representative will
receive as a commission.`,
  },
  {
    name: "commissionExempt",
    type: "boolean",
    description: `True if item should be exempt from commission, false if not.`,
  },
  {
    name: "customFields",
    type: "array of custom fields",
    description: `The list of custom fields for this object type.`,
  },
  {
    name: "uoms",
    type: "array of item units of measure",
    description: `The list of units of measure that can be used with this item.`,
    readOnly: true,
  },
  {
    name: "priceTiers",
    type: "array of price tiers",
    description: `The list of price tiers used for pricing this item.`,
    readOnly: true,
  },
  {
    name: "summaryOnly",
    type: "boolean",
    description: `True if the summary parameter was set when this record was retrieved. False if not.`,
    readOnly: true,
  },
  {
    name: "imageAsBase64String",
    type: "string",
    description: `Binary image of this item, encoded as a Base64 string.  This is only populated on a Read call, not on Query calls.`,
  },
  {
    name: "imageChanged",
    type: "boolean",
    description: `True if a new item image is being sent with this create or update transaction. False if not.`,
  },
];

export const QUERY_PARAMS = [
  {
    name: "start",
    type: "number",
    description: `A cursor used in pagination.  This is the row number of the full set of results. 
     The API limits results to a max of 200 results per call.  If you want to retrieve the next set
      of results you can use this parameter to retrive the next set of results.  For example if you
       are retrieving 200 results at a time, you can set start=201 to retrieve the next page of results.`,
  },
  {
    name: "maxresults",
    type: "number",
    description: `The maximum number of results you want to return.  The default is 200, the maximum value allowed.`,
  },
  {
    name: "summary",
    type: "",
    description: `If this parameter is present (the value doesn't matter, and doesn't need to be
     specified), only the summary attributes of the item will be returned.`,
  },
  {
    name: "query",
    type: "string",
    description: `This parameter will filter the results by matches of the string on the following fields: fullName, description, notes, barcode, sku, vendorPartNumber, or tags.`,
  },
  {
    name: "archived",
    type: "string: yes/no",
    description: `A "yes" returns archived records only; a "no" returns only those that have not been archived.`,
  },
  {
    name: "sku",
    type: "string",
    description: `Search for a single SKU.`,
  },
  {
    name: "tags",
    type: "string",
    description: `Returns records that match the provided comma separated list of tags.`,
  },
  {
    name: "type",
    type: "string",
    description: `Returns records that match the provided item type.`,
  },
  {
    name: "ids",
    type: "array of integers",
    description: `Filters the results by a list of item ids.  Example: ids=1,2,3`,
  },
  {
    name: "location",
    type: "string",
    description: `Filters items according to the name of the location.  This will also cause the inventory quantity values to reflect the totals at this location.`,
  },
  {
    name: "category",
    type: "string",
    description: `Filters items according to the category name.`,
  },
  {
    name: "createdsince/updatedsince",
    type: "string formatted as YYYY-MM-DDTHH:MM:SS ",
    description: `Filters transactions created or updated since a specified date/time.`,
  },
];

export const SAMPLE_QUERY_REQUEST =
  "/api/v2/item?start=101&maxresults=100&sku=409382304&location=warehouse&createdsince=2019-09-10T00:00:00&updatedsince=2019-09-10T23:59:59";

export const BOM_ATTRIBUTES = [
  {
    name: "item",
    type: "reference",
    description: "The item this BOM data represents.",
  },
  {
    name: "lines",
    type: "array of BOM line items",
    description: "The lines for this BOM. See object structure below.",
  },
];

export const LINE_ITEM = [
  {
    name: "lineId",
    type: "integer",
    description: "Unique identifier for this record.",
  },
  {
    name: "lineNumber",
    type: "integer",
    description: "The line number for this line on the BOM.",
  },
  {
    name: "componentItem",
    type: "reference",
    description: "A reference to the item for this line on the BOM.",
  },
  {
    name: "description",
    type: "string",
    description: "The item description.",
  },
  {
    name: "quantity",
    type: "integer",
    description: "The quantity of this item on the BOM.",
  },
  {
    name: "cost",
    type: "decimal",
    description: "The cost for this component item.",
  },
  {
    name: "total",
    type: "decimal",
    description: "The component item total.",
  },
  {
    name: "uomSetId",
    type: "decimal",
    description: "The UOM set id for this line on the BOM.",
  },
  {
    name: "typeOfItem",
    type: "string",
    description:
      'The kind of item. Must be one of the following: "Inventory Item", "Non-inventory", "Category", "Expense", "Assembly", "Kit", "Service", "Labor", "Overhead", or "Other".',
  },
  {
    name: "baseWeight",
    type: "decimal",
    description: "The base weight for this item",
    readOnly: true,
  },
  {
    name: "weight",
    type: "decimal",
    description: "The weight of this item.",
    readOnly: true,
  },
  {
    name: "weightunit",
    type: "string",
    description: "The unit for the weight value.",
    readOnly: true,
  },
  {
    name: "baseVolume",
    type: "decimal",
    description: "The base volume for this item",
    readOnly: true,
  },
  {
    name: "volume",
    type: "decimal",
    description: "The volume of this item.",
    readOnly: true,
  },
  {
    name: "volumeunit",
    type: "string",
    description: "The unit for the volume value.",
    readOnly: true,
  },
  {
    name: "notes",
    type: "string",
    description: "The note for this line on the BOM.",
  },
];

export const BOM_JSON = `{
  "data": {
    "item": 
      {
        "id": 851,
        "name": "Widget"
      },
    "lines": [
      {
        "id": 12,
        "lineNumber": 1,
        "componentItem": {
          "id": 123,
          "name": "widget-1"
        },
        "quantity": 1.00000,
        "cost": 14.00000,
        "total": 14.0000000000,
        "uomSetId": 0,
        "typeOfItem": "Inventory Item",
        "baseWeight": 0.00000,
        "weight": 5.0000000000,
        "weightUnit": "lb",
        "baseVolume": 0.00000,
        "volume": 3.0000000000,
        "volumeUnit": "cbm",
        "notes": null
      },
      {
        "id": 13,
        "lineNumber": 2,
        "componentItem": {
          "id": 234,
          "name": "widget-2"
        },
        "quantity": 3.00000,
        "cost": 5.00000,
        "total": 5.0000000000,
        "uomSetId": 0,
        "typeOfItem": "Inventory Item",
        "baseWeight": 1.00000,
        "weight": 1.0000000000,
        "weightUnit": "lb",
        "baseVolume": 1.00000,
        "volume": 1.0000000000,
        "volumeUnit": "cbm",
        "notes": "Bom line note"
      }
    ], 
  },
  "status": "ok",
  "message": ""
}`;
